import { useEffect, useRef } from 'react';

const criteria = [
  { label: 'Non-invasive', voice: 'Full', blood: 'None', imaging: 'None', questionnaire: 'Partial' },
  { label: 'Cost per test', voice: '$0.10 - 5', blood: '$50 - 500', imaging: '$500 - 3,000', questionnaire: '$0 - 50' },
  { label: 'Time to result', voice: '< 1 minute', blood: '1 - 3 days', imaging: 'Same day', questionnaire: '10 - 30 min' },
  { label: 'Frequency possible', voice: 'Continuous', blood: 'Weekly - Monthly', imaging: 'Quarterly - Annually', questionnaire: 'Visit-based' },
  { label: 'Objective measurement', voice: 'Full', blood: 'Full', imaging: 'Full', questionnaire: 'None' },
  { label: 'Patient burden', voice: 'None', blood: 'Moderate', imaging: 'Moderate-High', questionnaire: 'Low-Moderate' },
  { label: 'Scalability', voice: 'Excellent', blood: 'Limited', imaging: 'Poor', questionnaire: 'Good' },
];

export default function BiomarkerComparisonSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in-up');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.reveal');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative w-full bg-background-50 py-20 md:py-32 px-6 md:px-12 lg:px-20"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12 md:mb-16">
          <div className="reveal opacity-0 mb-4">
            <span className="font-body text-xs font-semibold tracking-widest text-accent-500 uppercase">
              Advantages Over Traditional Biomarkers
            </span>
          </div>
          <h2 className="reveal opacity-0 font-display text-3xl md:text-4xl lg:text-5xl font-medium text-foreground-50 leading-tight mb-4">
            What Makes Voice an Ideal Biomarker?
          </h2>
          <p className="reveal opacity-0 font-body text-base text-foreground-400 max-w-2xl mx-auto">
            Voice analysis outperforms traditional diagnostic methods across nearly every clinical dimension.
          </p>
        </div>

        {/* Desktop Table */}
        <div className="reveal opacity-0 hidden md:block overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="text-left font-body text-xs font-semibold tracking-wider text-foreground-500 uppercase py-4 px-4 border-b border-background-200/70">
                  Criterion
                </th>
                <th className="text-center font-body text-xs font-semibold tracking-wider text-primary-500 uppercase py-4 px-4 border-b border-background-200/70 bg-primary-500/5">
                  Voice Analysis
                </th>
                <th className="text-center font-body text-xs font-semibold tracking-wider text-foreground-500 uppercase py-4 px-4 border-b border-background-200/70">
                  Blood Tests
                </th>
                <th className="text-center font-body text-xs font-semibold tracking-wider text-foreground-500 uppercase py-4 px-4 border-b border-background-200/70">
                  Imaging
                </th>
                <th className="text-center font-body text-xs font-semibold tracking-wider text-foreground-500 uppercase py-4 px-4 border-b border-background-200/70">
                  Questionnaires
                </th>
              </tr>
            </thead>
            <tbody>
              {criteria.map((row, i) => (
                <tr key={i} className="hover:bg-background-100/50 transition-colors">
                  <td className="font-body text-sm text-foreground-50 py-4 px-4 border-b border-background-200/40">
                    {row.label}
                  </td>
                  <td className="text-center font-body text-sm font-medium text-primary-500 py-4 px-4 border-b border-background-200/40 bg-primary-500/5">
                    {row.voice}
                  </td>
                  <td className="text-center font-body text-sm text-foreground-400 py-4 px-4 border-b border-background-200/40">
                    {row.blood}
                  </td>
                  <td className="text-center font-body text-sm text-foreground-400 py-4 px-4 border-b border-background-200/40">
                    {row.imaging}
                  </td>
                  <td className="text-center font-body text-sm text-foreground-400 py-4 px-4 border-b border-background-200/40">
                    {row.questionnaire}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="reveal opacity-0 md:hidden flex flex-col gap-4">
          {criteria.map((row, i) => (
            <div key={i} className="p-4 rounded-xl bg-background-100 border border-background-200/70">
              <h3 className="font-body text-sm font-semibold text-foreground-50 mb-3">
                {row.label}
              </h3>
              <div className="grid grid-cols-2 gap-2">
                <div className="p-2 rounded-lg bg-primary-500/10">
                  <div className="font-body text-xs text-primary-500 mb-1">Voice Analysis</div>
                  <div className="font-body text-xs text-foreground-50 font-medium">{row.voice}</div>
                </div>
                <div className="p-2 rounded-lg bg-background-200/50">
                  <div className="font-body text-xs text-foreground-600 mb-1">Blood Tests</div>
                  <div className="font-body text-xs text-foreground-400">{row.blood}</div>
                </div>
                <div className="p-2 rounded-lg bg-background-200/50">
                  <div className="font-body text-xs text-foreground-600 mb-1">Imaging</div>
                  <div className="font-body text-xs text-foreground-400">{row.imaging}</div>
                </div>
                <div className="p-2 rounded-lg bg-background-200/50">
                  <div className="font-body text-xs text-foreground-600 mb-1">Questionnaires</div>
                  <div className="font-body text-xs text-foreground-400">{row.questionnaire}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}